import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, Truck, DollarSign, TrendingUp, TrendingDown, BarChart3,
  MapPin, Clock, CheckCircle, AlertCircle, Package, Users,
  Download, Calendar, Activity, Settings, Filter, Search,
  Lock, ArrowRight, ChevronRight, Star, Target, Percent, ExternalLink
} from 'lucide-react';

// ============ GROW COMMAND CENTER ============
// Dispatch Operations Command Center with full operational dashboard

// Subscription tier features - ALIGNED WITH PRICING PAGE
const GROW_TIERS = {
  none: { 
    name: 'Not Subscribed', 
    features: [],
    tabs: [],
    metrics: []
  },
  bronze: { 
    name: 'Bronze Dispatch', 
    price: 300,
    features: ['dispatch_dashboard', 'active_loads', 'basic_reports', 'weekly_summary'],
    // BRONZE: Basic dispatch, active loads tracking, weekly summary
    tabs: ['dashboard', 'loads', 'subscription'],
    metrics: ['weekly_gross', 'active_loads', 'deadhead'],
    reports: ['weekly_summary']
  },
  silver: { 
    name: 'Silver Dispatch', 
    price: 400,
    features: ['dispatch_dashboard', 'active_loads', 'revenue_reports', 'broker_performance', 'kpi_metrics', 'weekly_summary', 'monthly_reports'],
    // SILVER: Everything in Bronze + Revenue reports, Broker tracking, KPI metrics
    tabs: ['dashboard', 'loads', 'revenue', 'brokers', 'kpi', 'subscription'],
    metrics: ['weekly_gross', 'rpm_average', 'active_loads', 'broker_score', 'deadhead'],
    reports: ['weekly_summary', 'monthly_reports']
  },
  gold: { 
    name: 'Gold Dispatch', 
    price: 500,
    features: ['dispatch_dashboard', 'active_loads', 'revenue_reports', 'broker_performance', 'rate_history', 'kpi_metrics', 'weekly_summary', 'monthly_reports', 'revenue_forecasting', 'priority_dispatch', 'performance_intelligence', 'load_board'],
    // GOLD: Everything in Silver + Rate history, Load board, Revenue forecasting, Priority dispatch
    tabs: ['dashboard', 'loadboard', 'loads', 'revenue', 'brokers', 'rates', 'kpi', 'subscription'],
    metrics: ['weekly_gross', 'rpm_average', 'active_loads', 'broker_score', 'deadhead', 'acceptance_rate'],
    reports: ['weekly_summary', 'monthly_reports', 'revenue_forecasting']
  }
};

// Internal tabs for Grow - Will be filtered by tier
const ALL_GROW_TABS = [
  { id: 'dashboard', label: 'Dispatch Dashboard', icon: Activity },
  { id: 'loadboard', label: 'Load Board', icon: Package },
  { id: 'loads', label: 'Active Loads', icon: Truck },
  { id: 'revenue', label: 'Revenue Reports', icon: DollarSign },
  { id: 'brokers', label: 'Broker Performance', icon: Users },
  { id: 'rates', label: 'Rate History', icon: TrendingUp },
  { id: 'kpi', label: 'KPI Metrics', icon: Target },
  { id: 'subscription', label: 'Manage Subscription', icon: Settings }
];

// Mock dispatch data
const MOCK_DISPATCH_DATA = {
  weekly_gross: 8450,
  weekly_gross_change: 12,
  rpm_average: 2.87,
  rpm_change: 5,
  active_loads: 3,
  total_loads_week: 8,
  broker_score: 94,
  deadhead_percent: 8,
  acceptance_rate: 92,
  monthly_gross: 34200,
  loads: [
    { id: 'LD-2026-001', broker: 'CH Robinson', origin: 'Atlanta, GA', destination: 'Dallas, TX', miles: 780, rate: 2340, status: 'in_transit', pickup: '02/25/2026', delivery: '02/27/2026' },
    { id: 'LD-2026-002', broker: 'TQL', origin: 'Dallas, TX', destination: 'Phoenix, AZ', miles: 870, rate: 2610, status: 'booked', pickup: '02/28/2026', delivery: '03/01/2026' },
    { id: 'LD-2026-003', broker: 'Coyote', origin: 'Phoenix, AZ', destination: 'Los Angeles, CA', miles: 370, rate: 1110, status: 'pending', pickup: '03/02/2026', delivery: '03/02/2026' }
  ],
  brokers: [
    { name: 'CH Robinson', loads: 12, revenue: 14500, avg_rpm: 2.92, rating: 4.8, on_time_payment: 98 },
    { name: 'TQL', loads: 8, revenue: 9800, avg_rpm: 2.85, rating: 4.5, on_time_payment: 95 },
    { name: 'Coyote', loads: 6, revenue: 7200, avg_rpm: 2.78, rating: 4.3, on_time_payment: 92 },
    { name: 'Echo', loads: 4, revenue: 4800, avg_rpm: 2.70, rating: 4.0, on_time_payment: 88 },
    { name: 'Uber Freight', loads: 3, revenue: 3600, avg_rpm: 2.82, rating: 4.2, on_time_payment: 100 }
  ],
  rate_history: [
    { week: 'Feb 3', rpm: 2.72 },
    { week: 'Feb 10', rpm: 2.78 },
    { week: 'Feb 17', rpm: 2.81 },
    { week: 'Feb 24', rpm: 2.87 }
  ],
  kpi_data: {
    revenue_target: 40000,
    revenue_actual: 34200,
    load_target: 40,
    load_actual: 33,
    rpm_target: 2.80,
    rpm_actual: 2.87,
    deadhead_target: 10,
    deadhead_actual: 8
  }
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, trend, onClick, locked, prefix = '', suffix = '', drillDownKey }) => (
  <div 
    className={`bg-[#121212] border border-white/10 p-4 sm:p-6 ${onClick && !locked ? 'cursor-pointer hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5 transition-all' : ''} ${locked ? 'opacity-60' : ''}`}
    onClick={!locked ? onClick : undefined}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-white/50 text-xs sm:text-sm uppercase">{label}</span>
      {locked ? (
        <Lock className="w-4 h-4 text-white/30" />
      ) : (
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
      )}
    </div>
    <div className="flex items-end justify-between">
      <div className="flex items-end gap-2">
        <p className="text-xl sm:text-2xl font-bold text-white">{locked ? '—' : `${prefix}${value}${suffix}`}</p>
        {trend !== undefined && !locked && (
          <span className={`text-xs flex items-center gap-1 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      {onClick && !locked && (
        <ChevronRight className="w-4 h-4 text-white/30" />
      )}
    </div>
  </div>
);

// Load Status Badge
const LoadStatusBadge = ({ status }) => {
  const config = {
    in_transit: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Transit' },
    booked: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Booked' },
    pending: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Pending' },
    delivered: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Delivered' },
    cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' }
  };
  const c = config[status] || config.pending;
  return (
    <span className={`px-2 py-1 text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Dispatch Dashboard Tab - Shows tier-specific content
const DispatchDashboard = ({ data, onDrillDown, onTabChange, subscriptionTier }) => {
  const tierConfig = GROW_TIERS[subscriptionTier] || GROW_TIERS.bronze;
  const hasPerformanceIntelligence = tierConfig.features?.includes('performance_intelligence');
  
  return (
    <div className="space-y-6" data-testid="dispatch-dashboard">
      {/* Tier Badge */}
      <div className="flex items-center gap-3 mb-2">
        <span className="px-3 py-1 text-xs font-medium uppercase tracking-wider bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
          {tierConfig.name}
        </span>
        <span className="text-white/40 text-sm">
          {subscriptionTier === 'bronze' ? 'Basic dispatch tracking' : 
           subscriptionTier === 'silver' ? 'Enhanced analytics & reporting' :
           'Full premium dispatch services'}
        </span>
      </div>
      
      {/* Revenue Overview */}
      <div className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] border border-white/10 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold uppercase tracking-wider text-sm sm:text-base">Revenue This Week</h3>
          {(subscriptionTier === 'silver' || subscriptionTier === 'gold') && (
            <button 
              onClick={() => onTabChange && onTabChange('revenue')}
              className="text-[#D4AF37] text-xs sm:text-sm hover:text-white whitespace-nowrap transition-colors"
              data-testid="view-revenue-details"
            >
              View Details →
            </button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
          <div className="flex-shrink-0">
            <p className="text-3xl sm:text-4xl font-bold text-white">${data.weekly_gross.toLocaleString()}</p>
            <div className="flex items-center gap-2 mt-1">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-xs sm:text-sm">+{data.weekly_gross_change}% from last week</span>
            </div>
          </div>
          <div className={`flex-1 grid ${subscriptionTier === 'bronze' ? 'grid-cols-2' : 'grid-cols-3'} gap-2 sm:gap-4 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-6`}>
            <div className="text-center">
              <p className="text-white/50 text-[10px] sm:text-xs uppercase">Loads</p>
              <p className="text-lg sm:text-2xl font-bold text-white">{data.total_loads_week}</p>
            </div>
            {(subscriptionTier === 'silver' || subscriptionTier === 'gold') && (
              <div className="text-center">
                <p className="text-white/50 text-[10px] sm:text-xs uppercase">Avg RPM</p>
                <p className="text-lg sm:text-2xl font-bold text-white">${data.rpm_average}</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-white/50 text-[10px] sm:text-xs uppercase">Deadhead</p>
              <p className="text-lg sm:text-2xl font-bold text-white">{data.deadhead_percent}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Loads Quick View */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold uppercase tracking-wider">Active Loads</h3>
          <span className="text-[#D4AF37] text-sm">{data.active_loads} Active</span>
        </div>
        <div className="divide-y divide-white/5">
          {data.loads.slice(0, 3).map((load) => (
            <div key={load.id} className="flex items-center justify-between p-4 hover:bg-white/5">
              <div className="flex items-center gap-4">
                <Truck className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <p className="text-white font-medium">{load.id}</p>
                  <p className="text-white/50 text-sm">{load.origin} → {load.destination}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-green-400 font-bold">${load.rate.toLocaleString()}</p>
                  <p className="text-white/40 text-xs">{load.miles} mi</p>
                </div>
                <LoadStatusBadge status={load.status} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Intelligence - Gold only */}
      {hasPerformanceIntelligence ? (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-white font-bold uppercase tracking-wider mb-4">Performance Intelligence</h3>
          <div className="bg-[#0A0A0A] p-4 border-l-4 border-[#D4AF37]">
            <p className="text-white">
              <span className="text-[#D4AF37] font-bold">Insight:</span> Your RPM is trending upward (+{data.rpm_change}%). 
              Deadhead percentage is within optimal range at {data.deadhead_percent}%. 
              Consider diversifying broker relationships for rate stability.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <p className="text-white font-medium">Want AI-powered insights?</p>
              <p className="text-white/60 text-sm">
                Upgrade to Gold for Performance Intelligence, Rate History analysis, and the Queen B Curated Load Board.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Active Loads Tab
const ActiveLoads = ({ data }) => (
  <div className="space-y-6" data-testid="active-loads">
    <div className="bg-[#121212] border border-white/10">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-white font-bold uppercase tracking-wider">Load Board</h3>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 text-sm bg-white/5 text-white/60 hover:text-white">All</button>
          <button className="px-3 py-1 text-sm bg-[#D4AF37]/20 text-[#D4AF37]">Active</button>
          <button className="px-3 py-1 text-sm bg-white/5 text-white/60 hover:text-white">Completed</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-left text-white/50 text-xs uppercase">
              <th className="p-4">Load ID</th>
              <th className="p-4">Broker</th>
              <th className="p-4">Route</th>
              <th className="p-4">Miles</th>
              <th className="p-4">Rate</th>
              <th className="p-4">RPM</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.loads.map((load) => (
              <tr key={load.id} className="hover:bg-white/5">
                <td className="p-4 text-[#D4AF37] font-medium">{load.id}</td>
                <td className="p-4 text-white">{load.broker}</td>
                <td className="p-4 text-white">
                  <span className="block">{load.origin}</span>
                  <span className="text-white/50 text-sm">→ {load.destination}</span>
                </td>
                <td className="p-4 text-white">{load.miles}</td>
                <td className="p-4 text-green-400 font-bold">${load.rate.toLocaleString()}</td>
                <td className="p-4 text-white">${(load.rate / load.miles).toFixed(2)}</td>
                <td className="p-4"><LoadStatusBadge status={load.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Revenue Reports Tab
const RevenueReports = ({ data, subscriptionTier, userId }) => {
  const [downloading, setDownloading] = useState(null);
  const tierFeatures = GROW_TIERS[subscriptionTier]?.features || [];
  
  const handleDownloadReport = async (reportType) => {
    if (!userId) return;
    setDownloading(reportType);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      window.open(`${API}/grow/reports/download/${reportType}?user_id=${userId}`, '_blank');
    } catch (err) {
      console.error('Failed to download report:', err);
    } finally {
      setTimeout(() => setDownloading(null), 1000);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="revenue-reports">
      {/* Monthly Summary */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold uppercase tracking-wider">Monthly Revenue Summary</h3>
          <button 
            onClick={() => handleDownloadReport('monthly_reports')}
            disabled={downloading === 'monthly_reports' || !tierFeatures.includes('monthly_reports')}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962E] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading === 'monthly_reports' ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export PDF
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0A0A0A] p-4">
            <p className="text-white/50 text-xs uppercase">Gross Revenue</p>
            <p className="text-2xl font-bold text-white">${data.monthly_gross.toLocaleString()}</p>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <p className="text-white/50 text-xs uppercase">Total Loads</p>
            <p className="text-2xl font-bold text-white">{data.kpi_data.load_actual}</p>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <p className="text-white/50 text-xs uppercase">Avg RPM</p>
            <p className="text-2xl font-bold text-white">${data.rpm_average}</p>
          </div>
          <div className="bg-[#0A0A0A] p-4">
            <p className="text-white/50 text-xs uppercase">Deadhead %</p>
            <p className="text-2xl font-bold text-white">{data.deadhead_percent}%</p>
          </div>
        </div>
      </div>

      {/* Revenue Trend Chart Placeholder */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Revenue Trend</h3>
        <div className="h-48 flex items-end justify-around gap-2">
          {[8200, 7800, 9100, 8450, 8900, 9200, 8450].map((val, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-[#D4AF37] rounded-t"
                style={{ height: `${(val / 10000) * 100}%` }}
              />
              <span className="text-white/40 text-xs mt-2">W{i + 1}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Report Downloads */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Generate Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { id: 'weekly_summary', name: 'Weekly Revenue Report', desc: 'Detailed weekly breakdown', tier: 'bronze' },
            { id: 'monthly_reports', name: 'Monthly Performance Summary', desc: 'Full month analysis', tier: 'silver' },
            { id: 'revenue_forecasting', name: 'Revenue Forecast', desc: 'Projected earnings', tier: 'gold' }
          ].map((report) => {
            const isLocked = !tierFeatures.includes(report.id);
            const isDownloading = downloading === report.id;
            return (
              <div key={report.id} className={`bg-[#0A0A0A] border border-white/5 p-4 ${isLocked ? 'opacity-60' : 'hover:border-[#D4AF37]/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <Download className="w-5 h-5 text-[#D4AF37]" />
                  {isLocked && <Lock className="w-4 h-4 text-white/30" />}
                </div>
                <p className="text-white font-medium">{report.name}</p>
                <p className="text-white/50 text-sm mb-3">{report.desc}</p>
                <button 
                  onClick={() => !isLocked && handleDownloadReport(report.id)}
                  className={`w-full py-2 text-sm font-medium flex items-center justify-center gap-2 ${isLocked ? 'bg-white/5 text-white/30 cursor-not-allowed' : 'bg-[#D4AF37] text-black hover:bg-[#B8962E]'}`}
                  disabled={isLocked || isDownloading}
                  data-testid={`download-report-${report.id}`}
                >
                  {isDownloading ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  {isLocked ? `Requires ${report.tier.charAt(0).toUpperCase() + report.tier.slice(1)}` : isDownloading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Broker Performance Tab
const BrokerPerformance = ({ data }) => (
  <div className="space-y-6" data-testid="broker-performance">
    <div className="bg-[#121212] border border-white/10">
      <div className="p-4 border-b border-white/10">
        <h3 className="text-white font-bold uppercase tracking-wider">Broker Performance Tracker</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-left text-white/50 text-xs uppercase">
              <th className="p-4">Broker</th>
              <th className="p-4">Loads</th>
              <th className="p-4">Revenue</th>
              <th className="p-4">Avg RPM</th>
              <th className="p-4">Rating</th>
              <th className="p-4">Payment %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.brokers.map((broker, idx) => (
              <tr key={idx} className="hover:bg-white/5">
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white font-medium">{broker.name}</span>
                  </div>
                </td>
                <td className="p-4 text-white">{broker.loads}</td>
                <td className="p-4 text-green-400 font-bold">${broker.revenue.toLocaleString()}</td>
                <td className="p-4 text-white">${broker.avg_rpm.toFixed(2)}</td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                    <span className="text-white">{broker.rating}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`${broker.on_time_payment >= 95 ? 'text-green-400' : broker.on_time_payment >= 90 ? 'text-amber-400' : 'text-red-400'}`}>
                    {broker.on_time_payment}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Broker Diversification Chart */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Revenue by Broker</h3>
      <div className="space-y-3">
        {data.brokers.slice(0, 5).map((broker, idx) => {
          const totalRevenue = data.brokers.reduce((sum, b) => sum + b.revenue, 0);
          const percent = Math.round((broker.revenue / totalRevenue) * 100);
          return (
            <div key={idx}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-white">{broker.name}</span>
                <span className="text-white/60">{percent}%</span>
              </div>
              <div className="w-full h-2 bg-[#0A0A0A] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#D4AF37] rounded-full"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// Rate History Tab
const RateHistory = ({ data }) => {
  // Calculate min/max for better chart scaling
  const rpmValues = data.rate_history.map(item => item.rpm);
  const minRpm = Math.min(...rpmValues) - 0.1;
  const maxRpm = Math.max(...rpmValues) + 0.1;
  const range = maxRpm - minRpm;
  
  return (
  <div className="space-y-6" data-testid="rate-history">
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">RPM Trend</h3>
      <div className="h-48 flex items-end justify-around gap-4">
        {data.rate_history.map((item, i) => {
          // Calculate height as percentage based on data range (20% minimum visibility)
          const heightPercent = Math.max(20, ((item.rpm - minRpm) / range) * 80 + 20);
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className="text-white text-sm mb-2">${item.rpm}</div>
              <div 
                className="w-full bg-gradient-to-t from-[#D4AF37] to-[#D4AF37]/60 rounded-t min-h-[20px]"
                style={{ height: `${heightPercent}%` }}
              />
              <span className="text-white/40 text-xs mt-2">{item.week}</span>
            </div>
          );
        })}
      </div>
    </div>

    {/* Lane Analysis */}
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Top Performing Lanes</h3>
      <div className="space-y-3">
        {[
          { lane: 'Atlanta, GA → Dallas, TX', rpm: 3.00, loads: 5 },
          { lane: 'Dallas, TX → Phoenix, AZ', rpm: 2.95, loads: 4 },
          { lane: 'Phoenix, AZ → Los Angeles, CA', rpm: 2.88, loads: 3 },
          { lane: 'Los Angeles, CA → Seattle, WA', rpm: 2.75, loads: 2 }
        ].map((lane, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-[#0A0A0A] hover:bg-white/5">
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-white">{lane.lane}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/50 text-sm">{lane.loads} loads</span>
              <span className="text-green-400 font-bold">${lane.rpm}/mi</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
};

// KPI Metrics Tab
const KPIMetrics = ({ data }) => {
  const kpi = data.kpi_data;
  
  const KPIGauge = ({ label, actual, target, unit = '', inverse = false }) => {
    const percent = Math.min(100, (actual / target) * 100);
    const isGood = inverse ? actual <= target : actual >= target * 0.9;
    
    return (
      <div className="bg-[#0A0A0A] p-4">
        <p className="text-white/50 text-xs uppercase mb-2">{label}</p>
        <div className="flex items-end gap-2 mb-2">
          <p className="text-2xl font-bold text-white">{unit}{actual.toLocaleString()}</p>
          <p className="text-white/40 text-sm">/ {unit}{target.toLocaleString()}</p>
        </div>
        <div className="w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${isGood ? 'bg-green-500' : 'bg-amber-500'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className={`text-xs mt-1 ${isGood ? 'text-green-400' : 'text-amber-400'}`}>
          {Math.round(percent)}% of target
        </p>
      </div>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="kpi-metrics">
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPIGauge label="Monthly Revenue" actual={kpi.revenue_actual} target={kpi.revenue_target} unit="$" />
          <KPIGauge label="Total Loads" actual={kpi.load_actual} target={kpi.load_target} />
          <KPIGauge label="Average RPM" actual={kpi.rpm_actual} target={kpi.rpm_target} unit="$" />
          <KPIGauge label="Deadhead %" actual={kpi.deadhead_actual} target={kpi.deadhead_target} inverse={true} />
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Performance Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#0A0A0A] p-4 border-l-4 border-green-500">
            <p className="text-green-400 font-bold mb-1">Strengths</p>
            <ul className="text-white/70 text-sm space-y-1">
              <li>• RPM above target (+${(kpi.rpm_actual - kpi.rpm_target).toFixed(2)})</li>
              <li>• Deadhead below threshold (-{kpi.deadhead_target - kpi.deadhead_actual}%)</li>
              <li>• Strong broker relationships</li>
            </ul>
          </div>
          <div className="bg-[#0A0A0A] p-4 border-l-4 border-amber-500">
            <p className="text-amber-400 font-bold mb-1">Opportunities</p>
            <ul className="text-white/70 text-sm space-y-1">
              <li>• Revenue {Math.round((kpi.revenue_actual / kpi.revenue_target) * 100)}% to goal</li>
              <li>• {kpi.load_target - kpi.load_actual} more loads needed</li>
              <li>• Consider higher-RPM lanes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Subscription Management Tab
const SubscriptionManagement = ({ currentTier, onUpgrade }) => (
  <div className="space-y-6" data-testid="subscription-management">
    <div className="bg-[#121212] border border-white/10 p-6">
      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Current Plan</h3>
      <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#D4AF37]/30">
        <div>
          <p className="text-[#D4AF37] font-bold text-lg">{GROW_TIERS[currentTier]?.name || 'Not Subscribed'}</p>
          {GROW_TIERS[currentTier]?.price && (
            <p className="text-white/50">${GROW_TIERS[currentTier].price}/week</p>
          )}
        </div>
        <span className={`px-2 py-1 text-xs font-medium ${currentTier !== 'none' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {currentTier !== 'none' ? 'Active' : 'Not Subscribed'}
        </span>
      </div>
    </div>

    {currentTier === 'none' && (
      <div className="bg-[#121212] border border-white/10 p-6">
        <h3 className="text-white font-bold uppercase tracking-wider mb-4">Available Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(GROW_TIERS).filter(([k]) => k !== 'none').map(([key, tier]) => (
            <div key={key} className={`bg-[#0A0A0A] border ${key === 'gold' ? 'border-[#D4AF37]' : 'border-white/10'} p-4 hover:border-[#D4AF37]/30`}>
              {key === 'gold' && (
                <div className="text-[#D4AF37] text-xs font-bold uppercase mb-2">Most Popular</div>
              )}
              <p className="text-[#D4AF37] font-bold mb-1">{tier.name}</p>
              <p className="text-2xl font-bold text-white mb-4">${tier.price}<span className="text-sm text-white/50">/week</span></p>
              <button 
                onClick={() => onUpgrade(key)}
                className="w-full py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#B8962E]"
              >
                Activate Dispatch
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Load Board Tab - Queen B Curated Loads
const LoadBoard = ({ data }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRate, setFilterRate] = useState('all');
  
  // Mock curated loads from Queen B network
  const curatedLoads = [
    { id: 'QB-001', broker: 'Direct Shipper', origin: 'Atlanta, GA', destination: 'Nashville, TN', miles: 250, rate: 875, rpm: 3.50, pickup: '02/26/2026', delivery: '02/26/2026', equipment: 'Dry Van', weight: '24,000 lbs', curated: true },
    { id: 'QB-002', broker: 'Verified Partner', origin: 'Nashville, TN', destination: 'Chicago, IL', miles: 473, rate: 1560, rpm: 3.30, pickup: '02/26/2026', delivery: '02/27/2026', equipment: 'Dry Van', weight: '42,000 lbs', curated: true },
    { id: 'QB-003', broker: 'Direct Shipper', origin: 'Dallas, TX', destination: 'Houston, TX', miles: 239, rate: 717, rpm: 3.00, pickup: '02/27/2026', delivery: '02/27/2026', equipment: 'Flatbed', weight: '38,000 lbs', curated: true },
    { id: 'QB-004', broker: 'Trusted Broker', origin: 'Phoenix, AZ', destination: 'Los Angeles, CA', miles: 372, rate: 1116, rpm: 3.00, pickup: '02/27/2026', delivery: '02/27/2026', equipment: 'Reefer', weight: '35,000 lbs', curated: true },
    { id: 'QB-005', broker: 'Direct Shipper', origin: 'Miami, FL', destination: 'Orlando, FL', miles: 236, rate: 708, rpm: 3.00, pickup: '02/28/2026', delivery: '02/28/2026', equipment: 'Dry Van', weight: '18,000 lbs', curated: true },
    { id: 'QB-006', broker: 'Verified Partner', origin: 'Seattle, WA', destination: 'Portland, OR', miles: 174, rate: 522, rpm: 3.00, pickup: '02/28/2026', delivery: '02/28/2026', equipment: 'Dry Van', weight: '28,000 lbs', curated: true }
  ];
  
  return (
    <div className="space-y-6" data-testid="load-board">
      {/* Header with Queen B Badge */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/30 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <h3 className="text-white font-bold">Queen B Curated Load Board</h3>
              <p className="text-white/50 text-sm">Pre-vetted loads from our trusted network</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-medium">
            {curatedLoads.length} Available Loads
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/40" />
          <span className="text-white/40 text-sm">Filters:</span>
        </div>
        <select 
          value={filterRate}
          onChange={(e) => setFilterRate(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2"
        >
          <option value="all">All Rates</option>
          <option value="high">$3.00+ RPM</option>
          <option value="premium">$3.25+ RPM</option>
        </select>
        <button className="px-3 py-2 bg-white/5 text-white/60 text-sm hover:bg-white/10">
          Refresh Loads
        </button>
      </div>

      {/* Load Board Table */}
      <div className="bg-[#121212] border border-white/10">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0A0A0A]">
              <tr className="text-left text-white/50 text-xs uppercase">
                <th className="p-4">Load ID</th>
                <th className="p-4">Source</th>
                <th className="p-4">Route</th>
                <th className="p-4">Equipment</th>
                <th className="p-4">Miles</th>
                <th className="p-4">Rate</th>
                <th className="p-4">RPM</th>
                <th className="p-4">Pickup</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {curatedLoads.map((load) => (
                <tr key={load.id} className="hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#D4AF37] font-medium">{load.id}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400">
                      {load.broker}
                    </span>
                  </td>
                  <td className="p-4 text-white">
                    <span className="block">{load.origin}</span>
                    <span className="text-white/50 text-sm">→ {load.destination}</span>
                  </td>
                  <td className="p-4 text-white/70 text-sm">{load.equipment}</td>
                  <td className="p-4 text-white">{load.miles}</td>
                  <td className="p-4 text-green-400 font-bold">${load.rate.toLocaleString()}</td>
                  <td className="p-4">
                    <span className={`font-medium ${load.rpm >= 3.25 ? 'text-[#D4AF37]' : 'text-white'}`}>
                      ${load.rpm.toFixed(2)}
                    </span>
                  </td>
                  <td className="p-4 text-white/70 text-sm">{load.pickup}</td>
                  <td className="p-4">
                    <button className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#B8962E]">
                      Book Load
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load Board Info */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <h4 className="text-white font-bold mb-4">Why Queen B Curated Loads?</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium">Pre-Vetted</p>
              <p className="text-white/50 text-sm">All brokers verified for payment history</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium">Premium Rates</p>
              <p className="text-white/50 text-sm">Minimum $3.00/mile curated loads</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium">Quick Pay Available</p>
              <p className="text-white/50 text-sm">Most loads offer same-day payment</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Overlay Component
const PreviewOverlay = ({ onActivate }) => (
  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-10" data-testid="grow-preview-overlay">
    <div className="text-center max-w-md p-8">
      <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        GROW COMMAND CENTER
      </h2>
      <p className="text-white/60 mb-6">
        Premium dispatch operations to maximize your revenue and grow your business with expert load matching and rate negotiation.
      </p>
      <button 
        onClick={onActivate}
        className="px-8 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E]"
      >
        Activate Dispatch
      </button>
    </div>
  </div>
);

// ============ MAIN GROW COMMAND CENTER COMPONENT ============
const GrowCommandCenter = ({ 
  subscriptionStatus = 'none', 
  subscriptionTier = 'none',
  tierColor = '#D4AF37',
  onActivate,
  userId
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState(MOCK_DISPATCH_DATA);
  const isSubscribed = subscriptionStatus === 'active' || subscriptionStatus === 'selected' || subscriptionTier !== 'none';
  
  // Get tier configuration
  const tierConfig = GROW_TIERS[subscriptionTier] || GROW_TIERS.none;
  const availableTabs = tierConfig.tabs || [];
  const availableMetrics = tierConfig.metrics || [];
  
  // Filter tabs based on tier
  const GROW_TABS = ALL_GROW_TABS.filter(tab => 
    availableTabs.includes(tab.id)
  );

  // Handler for drill-down navigation
  const handleDrillDown = (metricKey) => {
    navigate(`/hive/drill-down/${metricKey}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DispatchDashboard data={data} onTabChange={setActiveTab} subscriptionTier={subscriptionTier} />;
      case 'loadboard':
        return <LoadBoard data={data} />;
      case 'loads':
        return <ActiveLoads data={data} />;
      case 'revenue':
        return <RevenueReports data={data} subscriptionTier={subscriptionTier} userId={userId} />;
      case 'brokers':
        return <BrokerPerformance data={data} />;
      case 'rates':
        return <RateHistory data={data} />;
      case 'kpi':
        return <KPIMetrics data={data} />;
      case 'subscription':
        return <SubscriptionManagement currentTier={subscriptionTier} onUpgrade={onActivate} />;
      default:
        return <DispatchDashboard data={data} subscriptionTier={subscriptionTier} />;
    }
  };

  return (
    <div className="relative" data-testid="grow-command-center">
      {/* Preview Overlay for non-subscribers */}
      {!isSubscribed && <PreviewOverlay onActivate={onActivate} />}
      
      {/* Header with Tier Badge */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            GROW
          </h1>
          {isSubscribed && (
            <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
              {tierConfig.name}
            </span>
          )}
        </div>
        <p className="text-[#D4AF37]">Dispatch Operations Command Center</p>
      </div>

      {/* Top Metric Cards - Filtered by tier */}
      <div className={`grid grid-cols-2 ${subscriptionTier === 'bronze' ? 'md:grid-cols-3' : subscriptionTier === 'silver' ? 'md:grid-cols-5' : 'md:grid-cols-6'} gap-3 mb-6`}>
        <StatCard 
          label="Weekly Gross" 
          value={data.weekly_gross.toLocaleString()} 
          prefix="$"
          icon={DollarSign} 
          color="#10B981"
          trend={data.weekly_gross_change}
          onClick={() => handleDrillDown('weekly-revenue')}
          drillDownKey="weekly-revenue"
        />
        {/* RPM - Silver+ only */}
        {(subscriptionTier === 'silver' || subscriptionTier === 'gold') && (
          <StatCard 
            label="RPM Avg" 
            value={data.rpm_average} 
            prefix="$"
            icon={TrendingUp} 
            color="#D4AF37"
            trend={data.rpm_change}
            onClick={() => handleDrillDown('rpm-analysis')}
            drillDownKey="rpm-analysis"
          />
        )}
        <StatCard 
          label="Active Loads" 
          value={data.active_loads} 
          icon={Truck} 
          color="#3B82F6"
          onClick={() => handleDrillDown('active-loads')}
          drillDownKey="active-loads"
        />
        {/* Broker Score - Silver+ only */}
        {(subscriptionTier === 'silver' || subscriptionTier === 'gold') && (
          <StatCard 
            label="Broker Score" 
            value={data.broker_score} 
            icon={Users} 
            color="#8B5CF6"
            onClick={() => handleDrillDown('broker-performance')}
            drillDownKey="broker-performance"
          />
        )}
        <StatCard 
          label="Deadhead" 
          value={data.deadhead_percent} 
          suffix="%"
          icon={Percent} 
          color={data.deadhead_percent <= 10 ? '#10B981' : '#F59E0B'}
        />
        {/* Acceptance Rate - Gold only */}
        {subscriptionTier === 'gold' && (
          <StatCard 
            label="Acceptance" 
            value={data.acceptance_rate} 
            suffix="%"
            icon={CheckCircle} 
            color="#10B981"
            onClick={() => handleDrillDown('kpi-details')}
            drillDownKey="kpi-details"
          />
        )}
      </div>

      {/* Internal Tabs - Filtered by tier */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
        {GROW_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`grow-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className={`${!isSubscribed ? 'filter blur-sm pointer-events-none' : ''}`}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default GrowCommandCenter;
